<script lang="ts" setup>
import { AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { PhoenixSocket, SOCKET_CONNECTION_ERROR } from '@shared/plugins/socket'
import { useGeneralStore } from '@shared/store/general'
import { useTypingIndicatorsStore } from '@shared/store/typing-indicators'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import { useUserLocale } from '@shared/utils/composables/locales'
import { INITIAL_PER_PAGE_VARIANTS } from '@shared/utils/constants/pagination'
import { BOT_TYPING_TIMEOUT } from '@shared/utils/constants/time'
import { handlePageVisibility } from '@shared/utils/helpers/app-visibility'
import { getFirstArrayItem } from '@shared/utils/helpers/array/get-first-array-item'
import {
  getErrorCodes,
  showErrorBasedOnCodeError,
} from '@shared/utils/helpers/errors/error-handling'
import { getErrorMessages } from '@shared/utils/helpers/errors/error-handling'
import { displayErrorMessages } from '@shared/utils/helpers/errors/error-message'
import { isMobileDevice } from '@shared/utils/helpers/is-mobile-device'
import {
  lsGet,
  LsGlobalKeys,
  lsSave,
} from '@shared/utils/helpers/local-storage'
import {
  SessionStorageKeys,
  sessionStorageRemove,
} from '@shared/utils/helpers/storage/session-storage'

import { Message } from '@protos/chat/message_pb'
import { MessageType } from '@protos/chat/message_type_pb'
import { ConversationStatus } from '@protos/conversation_enums_pb'
import { ApiErrorResponse } from '@protos/response/api_error_response_pb'

import { errorPredicates } from '@pc/helpers/error-predicates'
import routeNames from '@pc/router/route-names'
import { useConversationsStore } from '@pc/store/conversations'
import { useDemoModeStore } from '@pc/store/demo'
import { useMessagesStore } from '@pc/store/messages'
import { useSessionStore } from '@pc/store/session'
import { useSocketStore } from '@pc/store/socket'
import { JsonValue } from '@protobuf-ts/runtime'

const PatientChat = defineAsyncComponent(
  () => import('@pc/components/PatientChat.vue')
)

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const conversationsStore = useConversationsStore()
const demoModeStore = useDemoModeStore()
const socketStore = useSocketStore()
const videoCallConfigsStore = useVideoCallConfigsStore()
const messagesStore = useMessagesStore()
const sessionStore = useSessionStore()
const generalStore = useGeneralStore()
const { userLocale } = useUserLocale()

const { messages } = storeToRefs(messagesStore)
const { isFileUploading } = storeToRefs(generalStore)

const isConversationLoading = ref(false)
const chatContainer = ref<InstanceType<typeof PatientChat>>()

const conversationCantAcceptNewMessages = ref(false)

const routeConversationId = computed(() => Number(route.params.conversationId))
const isConversationPage = computed(() => route.name == routeNames.conversation)
const isQuickFormPage = computed(() => route.name == routeNames.quickFormPage)
const { currentConversation } = storeToRefs(conversationsStore)

const isChatOpenedDemo = computed(() => demoModeStore.isChatOpened)

const getMessages = async (messagesConversationId: number) => {
  return await messagesStore.fetchMessages({
    conversationId: messagesConversationId,
    page: 1,
    perPage: INITIAL_PER_PAGE_VARIANTS[2],
  })
}

const goToPreviousConversation = () => {
  const conversationId = lsGet(LsGlobalKeys.CurrentConversationId)
  conversationId &&
    router.push({
      name: routeNames.conversation,
      params: { conversationId },
      query: route?.query || {},
    })
}

const adjustRouteParams = (conversationId: number) => {
  if (conversationId === Number(route.params.conversationId)) {
    router.replace({
      params: { conversationId },
      query: route?.query || {},
    })
  }
}

const isNewConversation = (newConversationId: number) => {
  const previousConversationId = lsGet(LsGlobalKeys.CurrentConversationId)
  return String(newConversationId) !== previousConversationId
}

const removeStoredData = (newConversationId: number) => {
  if (isNewConversation(newConversationId)) {
    sessionStorageRemove(SessionStorageKeys.SelfShedulerUserInfo)
    videoCallConfigsStore.cleanCachedVideoConversation()
  }
}

const initConversations = async (newConversationId: number) => {
  await getMessages(newConversationId)
  isConversationLoading.value = false

  const isBotConversation =
    currentConversation.value &&
    currentConversation.value?.status === ConversationStatus.ASSIGNED_TO_BOT

  const isOneMessage =
    messages.value?.data?.data && messages.value?.data?.data?.length <= 1
  const lastMessage = messages.value?.data?.data
    ? getFirstArrayItem<Message>(messages.value?.data?.data as Message[])
    : null

  const isAssignedToBot =
    lastMessage &&
    lastMessage?.messageType === MessageType.ASSIGNEE_UPDATE_NOTIFICATION
  if (
    currentConversation.value?.id &&
    isBotConversation &&
    (isOneMessage || isAssignedToBot)
  ) {
    // set isTyping true for the Bot while first page loading
    const typingIndicatorsStore = useTypingIndicatorsStore()
    typingIndicatorsStore.setTypingStatus({
      conversationId: currentConversation.value.id,
      isTyping: true,
      timeout: BOT_TYPING_TIMEOUT,
    })
  }

  adjustRouteParams(newConversationId)
  chatContainer.value?.scrollToBottom()
}

const fetchConversation = async (
  conversationId: number,
  humansOnly: boolean
) => {
  await conversationsStore.startConversation({
    conversationId,
    data: {
      humansOnly,
      visitorSource: sessionStore.chatProps?.visitorSource,
      locale: userLocale.value,
    },
  })

  socketStore.subscribeConversationWithFetch(conversationId)
  lsSave(LsGlobalKeys.CurrentConversationId, String(conversationId))
  await initConversations(conversationId)
}

const onSocketClose = () => {
  ElMessage({
    type: 'error',
    grouping: true,
    message: t('pt.messages.errors.socketDisconnected'),
  })
}

const createSocketConnection = async () => {
  if (PhoenixSocket.connClosed() || !socketStore.isChatUserSubscribed()) {
    await socketStore.createSocketConnection(onSocketClose)
  }
}

const startConversation = async (newConversationId: number) => {
  if (
    newConversationId &&
    (isConversationPage.value || isQuickFormPage.value)
  ) {
    removeStoredData(newConversationId)
    conversationsStore.setCurrentConversation(newConversationId)
    const humansOnly = JSON.parse(route.query?.isHuman?.toString() ?? 'false')

    try {
      /**
       * @description
       * condition for demo page, not needed start conversation if chat is not opened in iframe
       * */
      if (conversationsStore.isIframePage && !isChatOpenedDemo.value) {
        return
      }

      isConversationLoading.value = true

      await createSocketConnection()

      await fetchConversation(newConversationId, humansOnly)
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.log('Error in startConversation', e)
      /* eslint-disable-next-line no-console */
      console.log(sessionStore)
      if (e === SOCKET_CONNECTION_ERROR) {
        await fetchConversation(newConversationId, humansOnly)
      }

      if (e instanceof AxiosError) {
        const error = e as AxiosError

        const errorResponse = ApiErrorResponse.fromJson(
          (error?.response?.data as JsonValue) || {}
        )
        const codes = getErrorCodes(errorResponse?.errors || [])

        const {
          isAutoCampaignDisabled,
          isCantAcceptNewMessages,
          isTacShouldBeAccepted,
        } = errorPredicates(codes)

        if (!isTacShouldBeAccepted) {
          /** @description
        / we need to show error message every time except error related to TAC
        / */
          showErrorBasedOnCodeError(e)
        }

        if (isAutoCampaignDisabled) {
          isConversationLoading.value = false
          return
        }

        if (codes && !isTacShouldBeAccepted) {
          const { response } = e
          const { errors } = ApiErrorResponse.fromJson(
            response?.data as JsonValue
          )

          const messages = getErrorMessages(errors)

          displayErrorMessages(messages, null, 5000)
        }

        if (!e || !isTacShouldBeAccepted) {
          conversationCantAcceptNewMessages.value = isCantAcceptNewMessages
          goToPreviousConversation()
          await initConversations(newConversationId)
        }
      } else {
        goToPreviousConversation()
        await initConversations(newConversationId)
      }
    } finally {
      isConversationLoading.value = false
    }
  }
}

watch(
  routeConversationId,
  async (newConversationId) => {
    await startConversation(newConversationId)
  },
  { immediate: true }
)

const onVisibleView = () => {
  if (routeConversationId.value) {
    if (PhoenixSocket.connClosed() || !socketStore.isChatUserSubscribed()) {
      if (PhoenixSocket.connClosed()) {
        socketStore.createSocketConnection(onSocketClose)
      } else {
        socketStore.subscribeChatUser()
      }
      getMessages(routeConversationId.value)
      socketStore.subscribeConversationWithFetch(routeConversationId.value)
    }

    isFileUploading.value = false
  }
}

const onHideView = () => {
  if (isMobileDevice() && !isFileUploading.value) {
    socketStore.closeSocketConnection()
    routeConversationId.value &&
      socketStore.unsubscribeConversation(routeConversationId.value)
  }
}

handlePageVisibility(onVisibleView, onHideView)

onBeforeRouteLeave(() => {
  socketStore.unsubscribeConversation(routeConversationId.value)
})
</script>

<template>
  <PatientChat
    v-if="isConversationPage"
    ref="chatContainer"
    :is-conversation-loading="isConversationLoading"
  />
  <router-view />
</template>
